import React from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Collapse,
  IconButton
} from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';

/**
 * ErrorDisplay Component
 * A reusable component for displaying errors with different styles and actions
 * 
 * @param {Object} props
 * @param {string} props.title - Error title (optional)
 * @param {string} props.message - Error message
 * @param {string} props.type - Error type: 'error', 'warning', 'info' (default: 'error')
 * @param {Function} props.onRetry - Retry function (optional)
 * @param {Function} props.onClose - Close function (optional)
 * @param {boolean} props.showRetry - Whether to show retry button (default: true)
 * @param {boolean} props.showClose - Whether to show close button (default: false)
 * @param {string} props.retryText - Custom retry button text (default: 'Retry')
 * @param {string} props.closeText - Custom close button text (default: 'Close')
 * @param {boolean} props.collapsible - Whether error can be collapsed (default: false)
 * @param {boolean} props.expanded - Whether error is expanded (default: true)
 * @param {Object} props.sx - Additional styles
 * @param {React.ReactNode} props.children - Additional content to display
 */
const ErrorDisplay = ({
  title,
  message,
  type = 'error',
  onRetry,
  onClose,
  showRetry = true,
  showClose = false,
  retryText = 'Retry',
  closeText = 'Close',
  collapsible = false,
  expanded = true,
  sx = {},
  children
}) => {
  const [isExpanded, setIsExpanded] = React.useState(expanded);

  const getErrorConfig = () => {
    switch (type) {
      case 'warning':
        return {
          severity: 'warning',
          icon: <WarningIcon />,
          color: 'warning',
          bgColor: 'warning.light',
          textColor: 'warning.contrastText'
        };
      case 'info':
        return {
          severity: 'info',
          icon: <InfoIcon />,
          color: 'info',
          bgColor: 'info.light',
          textColor: 'info.contrastText'
        };
      case 'error':
      default:
        return {
          severity: 'error',
          icon: <ErrorIcon />,
          color: 'error',
          bgColor: 'error.light',
          textColor: 'error.contrastText'
        };
    }
  };

  const config = getErrorConfig();

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // If no message and no children, don't render anything
  if (!message && !children) {
    return null;
  }

  return (
    <Collapse in={true}>
      <Alert
        severity={config.severity}
        icon={config.icon}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showRetry && onRetry && (
              <Button
                size="small"
                variant="contained"
                onClick={handleRetry}
                startIcon={<RefreshIcon />}
                sx={{
                  bgcolor: `${config.color}.dark`,
                  color: 'white',
                  '&:hover': {
                    bgcolor: `${config.color}.main`
                  }
                }}
              >
                {retryText}
              </Button>
            )}
            {showClose && onClose && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleClose}
                sx={{
                  borderColor: `${config.color}.main`,
                  color: `${config.color}.main`,
                  '&:hover': {
                    borderColor: `${config.color}.dark`,
                    bgcolor: `${config.color}.light`
                  }
                }}
              >
                {closeText}
              </Button>
            )}
            {collapsible && (
              <IconButton
                size="small"
                onClick={handleToggleExpand}
                sx={{ color: `${config.color}.main` }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        }
        sx={{
          borderRadius: 2,
          mb: 2,
          '& .MuiAlert-message': {
            width: '100%'
          },
          ...sx
        }}
      >
        <Box sx={{ width: '100%' }}>
          {title && (
            <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>
              {title}
            </AlertTitle>
          )}
          
          <Collapse in={isExpanded}>
            <Typography variant="body2" sx={{ mb: children ? 1 : 0 }}>
              {message}
            </Typography>
            
            {children && (
              <Box sx={{ mt: 1 }}>
                {children}
              </Box>
            )}
          </Collapse>
        </Box>
      </Alert>
    </Collapse>
  );
};

export default ErrorDisplay;
